import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { CASE_STUDIES } from '../../lib/constants';
import { Button } from '../ui/Button';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export const CaseStudies: React.FC = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const { current } = scrollContainerRef;
            const scrollAmount = current.offsetWidth * 0.8; // Scroll by 80% of the container width
            current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    return (
        <section className="py-32 bg-white">
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
                    <div>
                        <span className="text-[#11BC97] font-bold tracking-widest uppercase text-sm mb-4 block">Proven Results</span>
                        <h2 className="text-4xl md:text-5xl font-bold text-black">Case Studies</h2>
                    </div>
                    <div className="hidden md:flex items-center gap-4">
                        <Button variant="outline">View All Cases</Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => scroll('left')} className="w-14 h-14 !p-0"><ArrowLeft /></Button>
                            <Button variant="outline" onClick={() => scroll('right')} className="w-14 h-14 !p-0"><ArrowRight /></Button>
                        </div>
                    </div>
                </div>

                <div ref={scrollContainerRef} className="flex overflow-x-auto space-x-8 pb-8 no-scrollbar">
                    {CASE_STUDIES.map((study, idx) => (
                        <motion.div
                            key={study.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ delay: idx * 0.1 }}
                            className="group cursor-pointer flex-shrink-0 w-[300px] flex flex-col"
                        >
                            <div className="aspect-[3/4] overflow-hidden rounded-[2rem] border border-gray-100 relative mb-6 bg-gray-50">
                                <img
                                    src={study.image}
                                    alt={`Case Study ${study.id}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
                                <div className="absolute bottom-4 left-4">
                                    <span className="text-white text-sm font-bold uppercase tracking-wider bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                        Case 0{study.id}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-4 px-2 flex flex-col flex-grow">
                                <h3 className="text-xl font-bold text-gray-900">{study.sex}, {study.age}</h3>
                                <div className="space-y-3 flex-grow flex flex-col">
                                    <div>
                                        <h4 className="font-bold text-[#11BC97] tracking-wide text-xs uppercase mb-2">Condition</h4>
                                        <p className="text-sm text-gray-600 leading-relaxed">{study.condition}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-[#11BC97] tracking-wide text-xs uppercase mb-2">Solution</h4>
                                        <p className="text-sm text-gray-600 leading-relaxed">{study.solution}</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-12 md:hidden text-center">
                    <Button variant="outline">View All Cases</Button>
                </div>
            </div>
        </section>
    );
};